import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, Download, RefreshCw, ShieldCheck, ShieldX } from "lucide-react";

import api from "../api";
import { printReport } from "./reportPrint";

function amount(value) {
  return `Rs. ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function BalanceSheet() {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState({
    assets: [],
    liabilities: [],
    equity: [],
    total_assets: 0,
    total_liabilities: 0,
    total_equity: 0,
    total_liab_equity: 0,
    balanced: true,
    difference: 0,
    net_profit: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async (selectedDate = asOfDate) => {
    setLoading(true);
    try {
      const response = await api.get(`/accounts/reports/balance-sheet?as_of_date=${selectedDate}`);
      setReport(response.data || {});
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load balance sheet.");
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    loadReport(asOfDate);
  }, [asOfDate, loadReport]);

  const downloadPdf = () => {
    printReport({
      title: "Balance Sheet",
      subtitle: `As of ${report.as_of_date || asOfDate}`,
      summary: [
        { label: "Total Assets", value: amount(report.total_assets) },
        { label: "Liabilities", value: amount(report.total_liabilities) },
        { label: "Equity", value: amount(report.total_equity) },
        { label: "Status", value: report.balanced ? "Balanced" : `Difference ${amount(report.difference)}` },
      ],
      tables: [
        {
          title: "Assets",
          columns: ["Code", "Name", "Subtype", "Balance"],
          rows: (report.assets || []).map((row) => [row.code, row.name, row.subtype || "-", amount(row.balance)]),
        },
        {
          title: "Liabilities",
          columns: ["Code", "Name", "Subtype", "Balance"],
          rows: (report.liabilities || []).map((row) => [row.code, row.name, row.subtype || "-", amount(row.balance)]),
        },
        {
          title: "Equity",
          columns: ["Code", "Name", "Subtype", "Balance"],
          rows: (report.equity || []).map((row) => [row.code, row.name, row.subtype || "-", amount(row.balance)]),
        },
      ],
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Balance Sheet</h3>
            <p className="text-sm text-slate-500">Assets versus liabilities plus equity, including current-year P&amp;L rolled into equity.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={asOfDate} onChange={(event) => setAsOfDate(event.target.value)} className="input" />
            <button type="button" onClick={() => loadReport(asOfDate)} className="secondary-btn">
              <RefreshCw size={15} />
              Generate
            </button>
            <button type="button" onClick={downloadPdf} className="secondary-btn">
              <Download size={15} />
              Download as PDF
            </button>
          </div>
        </div>

        {error && <ErrorBanner message={error} />}

        <div className="grid gap-4 px-6 py-5 md:grid-cols-4">
          <SummaryCard label="Total Assets" value={report.total_assets} />
          <SummaryCard label="Liabilities" value={report.total_liabilities} />
          <SummaryCard label="Equity" value={report.total_equity} />
          <SummaryCard label="Current Year P&L" value={report.net_profit} />
        </div>

        <div className={`mx-6 mb-6 flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm ${report.balanced ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          {report.balanced ? <ShieldCheck size={18} /> : <ShieldX size={18} />}
          <span>
            {report.balanced
              ? `Balanced as of ${report.as_of_date || asOfDate}. Assets match liabilities plus equity.`
              : `Not balanced. Difference: ${amount(report.difference)}`}
          </span>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <SectionTable title="Assets" rows={report.assets} loading={loading} />
        <SectionTable title="Liabilities" rows={report.liabilities} loading={loading} />
        <SectionTable title="Equity" rows={report.equity} loading={loading} />
      </div>

      <style>{`
        .input {
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{amount(value)}</div>
    </div>
  );
}

function SectionTable({ title, rows = [], loading }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-5">
        <h4 className="text-lg font-bold text-slate-900">{title}</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Subtype</th>
              <th className="px-4 py-3">Balance</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.map((row) => (
              <tr key={`${title}-${row.code}`} className="border-t border-slate-100">
                <td className="px-4 py-3 font-semibold text-indigo-700">{row.code}</td>
                <td className="px-4 py-3">{row.name}</td>
                <td className="px-4 py-3">{row.subtype || "-"}</td>
                <td className="px-4 py-3 font-semibold">{amount(row.balance)}</td>
              </tr>
            ))}
            {!loading && !rows.length && (
              <tr>
                <td colSpan="4" className="px-4 py-10 text-center text-slate-500">No balances found.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan="4" className="px-4 py-10 text-center text-slate-500">Loading report...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="mx-6 mt-4 flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <AlertCircle size={16} />
      <span>{message}</span>
    </div>
  );
}

export default BalanceSheet;
